import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import dashboardService from '../../services/DashboardService.js';

const HeaderShipmentSearch = ({ onSelectShipment, variant = 'desktop' }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const isMobile = variant === 'mobile';

  useEffect(() => {
    const onClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await dashboardService.searchShipments(q);
        setResults(res?.data || []);
        setOpen(true);
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const goTo = (shipmentId) => {
    setOpen(false);
    setQuery('');
    if (onSelectShipment) onSelectShipment(shipmentId);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (results[0]?.shipment_id) {
      goTo(results[0].shipment_id);
    }
  };

  return (
    <form ref={wrapRef} onSubmit={onSubmit} className={`relative ${isMobile ? 'w-full' : 'w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]'}`}>
      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden w-full">
        <Search className="w-3.5 h-3.5 text-gray-400 ml-2 shrink-0" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('dashboard.searchGcOrMobile')}
          className="w-full min-w-0 bg-transparent px-1.5 py-1.5 text-xs sm:text-sm focus:outline-none"
        />
      </div>
      {open && (
        <div className={`absolute ${isMobile ? 'left-0 right-0' : 'right-0'} mt-1 w-full min-w-0 bg-white border border-gray-200 rounded-xl shadow-lg z-[70] max-h-72 overflow-y-auto`}>
          {loading && <p className="px-3 py-2 text-sm text-gray-400">{t('common.loading')}</p>}
          {!loading && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">{t('dashboard.noShipmentMatch')}</p>
          )}
          {results.map((row) => (
            <button
              key={row.shipment_id}
              type="button"
              onClick={() => goTo(row.shipment_id)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
            >
              <p className="text-sm font-medium text-blue-600 break-all">{row.shipment_id}</p>
              <p className="text-xs text-gray-500">
                {(row.from_hub_name || '-')} → {(row.to_hub_name || '-')} · {row.status || ''}
              </p>
            </button>
          ))}
        </div>
      )}
    </form>
  );
};

export default HeaderShipmentSearch;
